import * as React from "react";
import { useEffect, useRef } from "react";
import { Box, Container, Typography } from "@mui/material";
import TuneIcon         from "@mui/icons-material/Tune";
import SearchIcon       from "@mui/icons-material/Search";
import PhoneIcon        from "@mui/icons-material/Phone";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

const steps = [
  {
    Icon:  TuneIcon,
    label: "Step 1",
    title: "Set your criteria",
    desc:  "Choose niche, city, follower range, engagement benchmarks, and audience demographics for your campaign brief.",
    accentColor: "#FF6B35",
  },
  {
    Icon:  SearchIcon,
    label: "Step 2",
    title: "Discover creators",
    desc:  "Browse verified profiles with real audience analytics, authenticity scores, and content performance history.",
    accentColor: "#4A90E2",
  },
  {
    Icon:  PhoneIcon,
    label: "Step 3",
    title: "Get contact details",
    desc:  "Access verified phone numbers and email addresses instantly no back-and-forth, no middlemen, just direct outreach.",
    accentColor: "#22C55E",
  },
  {
    Icon:  RocketLaunchIcon,
    label: "Step 4",
    title: "Run and report",
    desc:  "Manage your campaign, track live post metrics, and deliver polished client reports all from one dashboard.",
    accentColor: "#F5A623",
  },
];

function LandingWorksSection() {
  return (
    <Box
      sx={{
        bgcolor:  "#060F24",
        position: "relative",
        overflow: "hidden",
        py:       { xs: "60px", md: "96px" },
        px:       { xs: "20px", md: "80px" },
      }}
    >
      {/* Dot-grid texture */}
      <Box sx={{
        position:        "absolute",
        inset:           0,
        backgroundImage: "radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)",
        backgroundSize:  "28px 28px",
        pointerEvents:   "none",
        zIndex:          0,
      }} />

      {/* Ambient glows */}
      <Box sx={{
        position:     "absolute",
        width:        480,
        height:       480,
        top:          "-80px",
        left:         "12%",
        background:   "radial-gradient(circle, rgba(255,107,53,0.09) 0%, rgba(255,107,53,0.03) 45%, transparent 70%)",
        pointerEvents: "none",
        zIndex:       0,
      }} />
      <Box sx={{
        position:     "absolute",
        width:        400,
        height:       400,
        bottom:       "20px",
        right:        "8%",
        background:   "radial-gradient(circle, rgba(74,144,226,0.09) 0%, rgba(74,144,226,0.03) 45%, transparent 70%)",
        pointerEvents: "none",
        zIndex:       0,
      }} />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>

        {/* Header */}
        <Box className="reveal" sx={{ textAlign: "center", mb: { xs: "48px", md: "72px" } }}>
          <Box component="span" sx={{
            display:       "inline-block",
            color:         "#FF6B35",
            bgcolor:       "rgba(255,107,53,0.12)",
            border:        "1px solid rgba(255,107,53,0.3)",
            px:            "14px",
            py:            "5px",
            borderRadius:  "100px",
            fontSize:      "10px",
            fontWeight:    700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            mb:            2,
          }}>
            How it works
          </Box>

          <Typography component="h2" sx={{
            fontSize:   "clamp(28px, 3.5vw, 40px)",
            fontWeight: 800,
            color:      "white",
            mb:         1.5,
            lineHeight: 1.2,
          }}>
            From brief to shortlist{" "}
            <Box component="span" sx={{
              background:           "linear-gradient(90deg, #FF6B35 0%, #FF9F6B 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor:  "transparent",
              backgroundClip:       "text",
            }}>
              in 5 minutes.
            </Box>
          </Typography>
          <Typography sx={{ fontSize: "16px", color: "rgba(255,255,255,0.45)" }}>
            No training needed. No spreadsheets. Just results.
          </Typography>
        </Box>

        {/* Steps grid */}
        <Box sx={{
          display:             "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
          gap:                 "20px",
          position:            "relative",
        }}>
          {/* Connector line — desktop only, passes through card icon centers */}
          <Box sx={{
            display:    { xs: "none", md: "block" },
            position:   "absolute",
            top:        "57px",
            left:       "calc(12.5% + 10px)",
            right:      "calc(12.5% + 10px)",
            height:     "2px",
            background: "linear-gradient(90deg, #FF6B35 0%, rgba(255,107,53,0.4) 35%, rgba(74,144,226,0.4) 65%, #F5A623 100%)",
            opacity:    0.45,
            zIndex:     0,
          }} />

          {steps.map((step, i) => (
            <Box
              key={i}
              className={`reveal reveal-delay-${i + 1}`}
              sx={{
                position:       "relative",
                zIndex:         1,
                display:        "flex",
                flexDirection:  "column",
                alignItems:     "center",
                textAlign:      "center",
                bgcolor:        "rgba(255,255,255,0.03)",
                border:         "1px solid rgba(255,255,255,0.07)",
                borderRadius:   "20px",
                p:              { xs: "24px 16px", md: "32px 20px 28px" },
                transition:     "all 0.3s ease",
                "&:hover": {
                  bgcolor:     "rgba(255,255,255,0.06)",
                  borderColor: `${step.accentColor}44`,
                  transform:   "translateY(-6px)",
                  boxShadow:   `0 20px 48px rgba(0,0,0,0.3), 0 0 0 1px ${step.accentColor}22`,
                },
              }}
            >
              {/* Corner glow */}
              <Box sx={{
                position:     "absolute",
                top:          -30,
                right:        -30,
                width:        90,
                height:       90,
                borderRadius: "50%",
                background:   `radial-gradient(circle, ${step.accentColor}14, transparent 70%)`,
                pointerEvents: "none",
              }} />

              {/* Icon circle with step number badge */}
              <Box sx={{ position: "relative", mb: "18px", flexShrink: 0 }}>
                <Box sx={{
                  width:          54,
                  height:         54,
                  borderRadius:   "50%",
                  bgcolor:        `${step.accentColor}14`,
                  border:         `1.5px solid ${step.accentColor}40`,
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  position:       "relative",
                  zIndex:         2,
                }}>
                  <step.Icon sx={{ color: step.accentColor, fontSize: 24 }} />
                </Box>
                {/* Step number badge */}
                <Box sx={{
                  position:       "absolute",
                  top:            -6,
                  right:          -6,
                  width:          20,
                  height:         20,
                  borderRadius:   "50%",
                  bgcolor:        step.accentColor,
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  fontSize:       "10px",
                  fontWeight:     800,
                  color:          "white",
                  zIndex:         3,
                  border:         "1.5px solid #060F24",
                }}>
                  {i + 1}
                </Box>
              </Box>

              <Typography sx={{
                fontSize:   "15px",
                fontWeight: 700,
                color:      "white",
                mb:         "8px",
                lineHeight: 1.3,
              }}>
                {step.title}
              </Typography>
              <Typography sx={{
                fontSize:   "13px",
                color:      "rgba(255,255,255,0.48)",
                lineHeight: 1.65,
              }}>
                {step.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default LandingWorksSection;
